import * as React from "react";
import { cn } from "./cn";
import { ImageLightbox } from "./image-lightbox";

export interface GalleryImage {
  /** R2 object key persisted on the org. */
  key: string;
  /** Resolved public URL for display. */
  url: string;
}

export interface ImageGalleryEditorLabels {
  /** Tile CTA, e.g. "Thêm ảnh". */
  add: string;
  /** Counter line, receives current count + max, e.g. (n,max) => `${n}/${max} ảnh`. */
  counter: (count: number, max: number) => string;
  /** Aria-label for the remove (X) button. */
  remove: string;
  /** Aria-label for the move-earlier button. */
  moveBack: string;
  /** Aria-label for the move-later button. */
  moveForward: string;
  /** Shown inline when an upload fails. */
  uploadError: string;
  /** Optional helper text under the grid. */
  hint?: string;
  /** Optional badge on the first image marking it as the cover, e.g. "Ảnh bìa". */
  cover?: string;
  /** Aria-label for opening an image preview. Defaults to "Xem ảnh". */
  view?: string;
  /** Aria-label for the lightbox close button. Defaults to "Đóng". */
  close?: string;
  /** Aria-label for the lightbox previous-image button. */
  prev?: string;
  /** Aria-label for the lightbox next-image button. */
  next?: string;
}

export interface ImageGalleryEditorProps {
  /** Ordered images. Index = display order. */
  images: GalleryImage[];
  /**
   * Presign + PUT one file, resolve to the new image. Thrown errors surface
   * inline. `onProgress` receives the upload percent (0–100) for that file —
   * forward it to the upload hook to drive the percent overlay.
   */
  onUpload: (
    file: File,
    onProgress: (percent: number) => void,
  ) => Promise<GalleryImage>;
  /** Called with the next ordered list after add / remove / reorder. */
  onChange: (next: GalleryImage[]) => void;
  labels: ImageGalleryEditorLabels;
  /** Hard cap. Default 10. */
  max?: number;
  /** Disable all editing (e.g. non-owner). */
  disabled?: boolean;
  /** Accepted MIME types for the file picker. */
  accept?: string;
  className?: string;
}

const DEFAULT_ACCEPT = "image/jpeg,image/png,image/webp";

// Presentational, controlled gallery editor. No data fetching — the caller
// injects `onUpload` (presign + PUT) and `onChange`. Reordering uses explicit
// move buttons rather than HTML5 drag so it works the same on touch + mouse.
// Shared by the employer org-settings page and the admin org-detail dialog.
export function ImageGalleryEditor({
  images,
  onUpload,
  onChange,
  labels,
  max = 10,
  disabled = false,
  accept = DEFAULT_ACCEPT,
  className,
}: ImageGalleryEditorProps) {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [busy, setBusy] = React.useState(false);
  const [preview, setPreview] = React.useState<number | null>(null);
  const [progress, setProgress] = React.useState(0);
  // For multi-file selections, show "2/5" alongside the current file's percent.
  const [batch, setBatch] = React.useState<{ index: number; total: number }>({
    index: 0,
    total: 0,
  });
  const [error, setError] = React.useState(false);

  const remaining = Math.max(0, max - images.length);
  const canAdd = !disabled && remaining > 0;

  async function handleFiles(fileList: FileList | null) {
    if (!fileList || fileList.length === 0) return;
    setError(false);
    // Cap to remaining slots, upload sequentially so order is deterministic.
    const files = Array.from(fileList).slice(0, remaining);
    setBatch({ index: 0, total: files.length });
    setProgress(0);
    setBusy(true);
    const added: GalleryImage[] = [];
    try {
      for (let i = 0; i < files.length; i++) {
        setBatch({ index: i + 1, total: files.length });
        setProgress(0);
        added.push(await onUpload(files[i]!, setProgress));
      }
      if (added.length > 0) onChange([...images, ...added]);
    } catch {
      setError(true);
      // Still keep any images that uploaded before the failure.
      if (added.length > 0) onChange([...images, ...added]);
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  }

  function removeAt(index: number) {
    onChange(images.filter((_, i) => i !== index));
  }

  function move(index: number, delta: number) {
    const target = index + delta;
    if (target < 0 || target >= images.length) return;
    const next = [...images];
    const [item] = next.splice(index, 1);
    next.splice(target, 0, item!);
    onChange(next);
  }

  return (
    <div className={cn("space-y-3", className)}>
      <div className="grid grid-cols-3 gap-3 sm:grid-cols-4 md:grid-cols-5">
        {images.map((img, i) => (
          <div
            key={img.key}
            className="group relative aspect-square overflow-hidden rounded-xl border border-smoke bg-mist"
          >
            <button
              type="button"
              aria-label={labels.view ?? "Xem ảnh"}
              onClick={() => setPreview(i)}
              className="block h-full w-full cursor-zoom-in"
            >
              <img
                src={img.url}
                alt=""
                className="h-full w-full object-cover"
                loading="lazy"
              />
            </button>
            {!disabled ? (
              <div className="pointer-events-none absolute inset-0 flex flex-col justify-between p-1.5 opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100 [@media(hover:none)]:opacity-100">
                <div className="flex justify-end">
                  <button
                    type="button"
                    aria-label={labels.remove}
                    onClick={() => removeAt(i)}
                    className="pointer-events-auto flex h-7 w-7 items-center justify-center rounded-full bg-ink/80 text-paper hover:bg-danger"
                  >
                    <XIcon />
                  </button>
                </div>
                <div className="flex justify-between">
                  <button
                    type="button"
                    aria-label={labels.moveBack}
                    disabled={i === 0}
                    onClick={() => move(i, -1)}
                    className="pointer-events-auto flex h-7 w-7 items-center justify-center rounded-full bg-ink/80 text-paper hover:bg-ink disabled:opacity-30"
                  >
                    <ChevronIcon dir="left" />
                  </button>
                  <button
                    type="button"
                    aria-label={labels.moveForward}
                    disabled={i === images.length - 1}
                    onClick={() => move(i, 1)}
                    className="pointer-events-auto flex h-7 w-7 items-center justify-center rounded-full bg-ink/80 text-paper hover:bg-ink disabled:opacity-30"
                  >
                    <ChevronIcon dir="right" />
                  </button>
                </div>
              </div>
            ) : null}
            {i === 0 && labels.cover ? (
              <span className="absolute bottom-1.5 left-1.5 rounded-full bg-lime px-2 py-0.5 text-[10px] font-bold text-ink">
                {labels.cover}
              </span>
            ) : null}
          </div>
        ))}

        {canAdd ? (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={busy}
            className="flex aspect-square flex-col items-center justify-center gap-1.5 rounded-xl border-2 border-dashed border-smoke px-2 text-ink/60 transition-colors hover:border-ink hover:text-ink disabled:opacity-80"
          >
            {busy ? (
              <>
                <span className="text-sm font-bold tabular-nums text-ink">
                  {progress}%
                </span>
                <span className="h-1 w-full overflow-hidden rounded-full bg-smoke">
                  <span
                    className="block h-full rounded-full bg-lime transition-[width] duration-150"
                    style={{ width: `${progress}%` }}
                  />
                </span>
                {batch.total > 1 ? (
                  <span className="text-[10px] font-semibold tabular-nums text-ink/50">
                    {batch.index}/{batch.total}
                  </span>
                ) : null}
              </>
            ) : (
              <>
                <PlusIcon />
                <span className="px-1 text-center text-xs font-semibold">
                  {labels.add}
                </span>
              </>
            )}
          </button>
        ) : null}
      </div>

      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple
        className="hidden"
        onChange={(e) => void handleFiles(e.target.files)}
      />

      <div className="flex items-center justify-between gap-2">
        <span className="text-xs text-ink/50">
          {labels.counter(images.length, max)}
        </span>
        {error ? (
          <span className="text-xs font-semibold text-danger">
            {labels.uploadError}
          </span>
        ) : null}
      </div>
      {labels.hint ? (
        <p className="text-xs text-ink/50">{labels.hint}</p>
      ) : null}

      <ImageLightbox
        images={images.map((img) => img.url)}
        index={preview}
        onClose={() => setPreview(null)}
        onIndexChange={setPreview}
        labels={{
          close: labels.close ?? "Đóng",
          prev: labels.prev,
          next: labels.next,
        }}
      />
    </div>
  );
}

function XIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M6 6l12 12M18 6L6 18"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

function PlusIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M12 5v14M5 12h14"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

function ChevronIcon({ dir }: { dir: "left" | "right" }) {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden
      style={dir === "right" ? { transform: "scaleX(-1)" } : undefined}
    >
      <path
        d="M15 6l-6 6 6 6"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
